import * as THREE from 'three';
import { NPCEntityType, type NPCSpawnData, type NPCPosData } from './protocol';

// --- Character definitions ---

interface CharacterDef {
  name: string;
  gender: 'male' | 'female';
  skinColor: number;
  hairColor: number;
  shirtColor: number;
  pantsColor: number;
  shoeColor: number;
  eyeColor: number;
  scale: number;
}

// The player is Janne, so Janne is not an NPC.
// Girls: Rosie, Marcelle, Mathilda, Grote Floor, Floor, Minne, Dores
// Boys: Onno, Jules
const CHARACTERS: CharacterDef[] = [
  {
    name: 'Rosie',
    gender: 'female',
    skinColor: 0xf8d5b8,
    hairColor: 0xc0392b,
    shirtColor: 0xff69b4,
    pantsColor: 0x8e44ad,
    shoeColor: 0xf0e0d0,
    eyeColor: 0x27ae60,
    scale: 1.0,
  },
  {
    name: 'Marcelle',
    gender: 'female',
    skinColor: 0xe8b88a,
    hairColor: 0x1a1a2e,
    shirtColor: 0x2ecc71,
    pantsColor: 0x1a5276,
    shoeColor: 0x2c2c2c,
    eyeColor: 0x6e4b3a,
    scale: 1.0,
  },
  {
    name: 'Onno',
    gender: 'female',
    skinColor: 0xf0c8a0,
    hairColor: 0xe6b800,
    shirtColor: 0xf39c12,
    pantsColor: 0x555555,
    shoeColor: 0x444444,
    eyeColor: 0x3498db,
    scale: 1.0,
  },
  {
    name: 'Jules',
    gender: 'male',
    skinColor: 0xf5d0b0,
    hairColor: 0x333333,
    shirtColor: 0x1abc9c,
    pantsColor: 0x2c3e50,
    shoeColor: 0x222222,
    eyeColor: 0x884422,
    scale: 1.0,
  },
  {
    name: 'Mathilda',
    gender: 'female',
    skinColor: 0xf2c8a8,
    hairColor: 0xd4a017,
    shirtColor: 0xe74c3c,
    pantsColor: 0x6c3483,
    shoeColor: 0xcc9966,
    eyeColor: 0x1e8449,
    scale: 1.0,
  },
  {
    name: 'Grote Floor',
    gender: 'female',
    skinColor: 0xeebb99,
    hairColor: 0x8b4513,
    shirtColor: 0x3498db,
    pantsColor: 0x7f8c8d,
    shoeColor: 0x5d4e37,
    eyeColor: 0x2e86c1,
    scale: 1.45,
  },
  {
    name: 'Floor',
    gender: 'female',
    skinColor: 0xf0c8a0,
    hairColor: 0xb07030,
    shirtColor: 0x5dade2,
    pantsColor: 0x48c9b0,
    shoeColor: 0xddbbaa,
    eyeColor: 0x2e86c1,
    scale: 1.0,
  },
  {
    name: 'Minne',
    gender: 'female',
    skinColor: 0xf8d8c0,
    hairColor: 0xf0c050,
    shirtColor: 0xf1c40f,
    pantsColor: 0xe67e22,
    shoeColor: 0xf5e0c0,
    eyeColor: 0x5499c7,
    scale: 0.85,
  },
  {
    name: 'Dores',
    gender: 'female',
    skinColor: 0xe8c098,
    hairColor: 0x2c2c2c,
    shirtColor: 0x9b59b6,
    pantsColor: 0x154360,
    shoeColor: 0x333333,
    eyeColor: 0x5d4e37,
    scale: 1.0,
  },
];

// --- NPC voxel body builder ---

function createNameTag(name: string): THREE.Sprite {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  canvas.width = 256;
  canvas.height = 64;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.font = 'bold 32px sans-serif';
  const measured = ctx.measureText(name);
  const bgWidth = Math.min(measured.width + 30, 250);
  const bgX = (canvas.width - bgWidth) / 2;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.beginPath();
  ctx.roundRect(bgX, 6, bgWidth, 44, 8);
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, canvas.width / 2, canvas.height / 2 - 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  const material = new THREE.SpriteMaterial({ map: texture, depthTest: false, sizeAttenuation: true });
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(2.2, 0.55, 1);
  return sprite;
}

function buildCharacterMesh(def: CharacterDef): THREE.Group {
  const root = new THREE.Group();
  const s = def.scale;
  const v = 0.25 * s;
  const isFemale = def.gender === 'female';

  function box(w: number, h: number, d: number, color: number): THREE.Mesh {
    const geometry = new THREE.BoxGeometry(w * v, h * v, d * v);
    const material = new THREE.MeshLambertMaterial({ color });
    return new THREE.Mesh(geometry, material);
  }

  const bodyGroup = new THREE.Group();
  bodyGroup.name = 'bodyGroup';
  root.add(bodyGroup);

  // --- Head ---
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  const head = box(3.2, 3.2, 3.2, def.skinColor);
  headGroup.add(head);

  // Hair - girls get longer, more styled hair
  if (isFemale) {
    // Longer hair on top
    const hairTop = box(3.5, 1.2, 3.5, def.hairColor);
    hairTop.position.y = 1.2 * v;
    headGroup.add(hairTop);

    // Hair sides (framing the face)
    const hairLeft = box(0.5, 3.0, 3.2, def.hairColor);
    hairLeft.position.set(-1.6 * v, -0.3 * v, 0);
    headGroup.add(hairLeft);

    const hairRight = box(0.5, 3.0, 3.2, def.hairColor);
    hairRight.position.set(1.6 * v, -0.3 * v, 0);
    headGroup.add(hairRight);

    // Long hair back (flowing down)
    const hairBack = box(3.5, 4.5, 0.7, def.hairColor);
    hairBack.position.set(0, -1.5 * v, -1.5 * v);
    headGroup.add(hairBack);
  } else {
    // Short male hair
    const hairTop = box(3.4, 1.0, 3.4, def.hairColor);
    hairTop.position.y = 1.1 * v;
    headGroup.add(hairTop);

    const hairBack = box(3.4, 2.0, 0.6, def.hairColor);
    hairBack.position.set(0, 0, -1.5 * v);
    headGroup.add(hairBack);
  }

  // Eyes
  const eyeGeo = new THREE.BoxGeometry(0.5 * v, 0.5 * v, 0.15 * v);
  const eyeMat = new THREE.MeshLambertMaterial({ color: def.eyeColor });
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.55 * v, 0.15 * v, 1.65 * v);
  headGroup.add(leftEye);

  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.55 * v, 0.15 * v, 1.65 * v);
  headGroup.add(rightEye);

  // Eyelashes for girls
  if (isFemale) {
    const lashGeo = new THREE.BoxGeometry(0.6 * v, 0.15 * v, 0.15 * v);
    const lashMat = new THREE.MeshLambertMaterial({ color: 0x222222 });
    const leftLash = new THREE.Mesh(lashGeo, lashMat);
    leftLash.position.set(-0.55 * v, 0.45 * v, 1.66 * v);
    headGroup.add(leftLash);

    const rightLash = new THREE.Mesh(lashGeo, lashMat);
    rightLash.position.set(0.55 * v, 0.45 * v, 1.66 * v);
    headGroup.add(rightLash);
  }

  // Mouth - girls get a slightly pink mouth
  const mouthColor = isFemale ? 0xcc5566 : 0x884444;
  const mouthGeo = new THREE.BoxGeometry(1.0 * v, 0.25 * v, 0.15 * v);
  const mouthMat = new THREE.MeshLambertMaterial({ color: mouthColor });
  const mouth = new THREE.Mesh(mouthGeo, mouthMat);
  mouth.position.set(0, -0.65 * v, 1.65 * v);
  headGroup.add(mouth);

  headGroup.position.y = (4.0 + 3.2 + 1.6) * v;
  bodyGroup.add(headGroup);

  // --- Torso ---
  if (isFemale) {
    // Slightly narrower waist, dress/skirt extends down
    const torsoUpper = box(3.0, 2.5, 2.0, def.shirtColor);
    torsoUpper.position.y = (4.0 + 2.75) * v;
    bodyGroup.add(torsoUpper);

    // Skirt/dress bottom - flares out
    const skirt = box(3.6, 2.5, 2.4, def.shirtColor);
    skirt.position.y = (4.0 + 0.5) * v;
    bodyGroup.add(skirt);
  } else {
    const torso = box(3.0, 4.0, 2.0, def.shirtColor);
    torso.position.y = (4.0 + 2.0) * v;
    bodyGroup.add(torso);
  }

  // --- Arms ---
  const armWidth = isFemale ? 0.85 : 1.0;
  const leftArmPivot = new THREE.Group();
  leftArmPivot.name = 'leftArm';
  const leftArm = box(armWidth, 4.0, armWidth, def.shirtColor);
  const leftHand = box(armWidth, 1.0, armWidth, def.skinColor);
  leftHand.position.y = -2.5 * v;
  leftArmPivot.add(leftArm);
  leftArmPivot.add(leftHand);
  leftArmPivot.position.set(-2.0 * v, (4.0 + 3.5) * v, 0);
  bodyGroup.add(leftArmPivot);

  const rightArmPivot = new THREE.Group();
  rightArmPivot.name = 'rightArm';
  const rightArm = box(armWidth, 4.0, armWidth, def.shirtColor);
  const rightHand = box(armWidth, 1.0, armWidth, def.skinColor);
  rightHand.position.y = -2.5 * v;
  rightArmPivot.add(rightArm);
  rightArmPivot.add(rightHand);
  rightArmPivot.position.set(2.0 * v, (4.0 + 3.5) * v, 0);
  bodyGroup.add(rightArmPivot);

  // --- Legs ---
  const legWidth = isFemale ? 1.0 : 1.2;
  const leftLegPivot = new THREE.Group();
  leftLegPivot.name = 'leftLeg';
  const leftLeg = box(legWidth, 3.5, legWidth, def.pantsColor);
  leftLeg.position.y = -1.75 * v;
  leftLegPivot.add(leftLeg);
  const leftShoe = box(legWidth, 0.8, 1.4, def.shoeColor);
  leftShoe.position.set(0, -3.75 * v, 0.1 * v);
  leftLegPivot.add(leftShoe);
  leftLegPivot.position.set(-0.6 * v, 4.0 * v, 0);
  bodyGroup.add(leftLegPivot);

  const rightLegPivot = new THREE.Group();
  rightLegPivot.name = 'rightLeg';
  const rightLeg = box(legWidth, 3.5, legWidth, def.pantsColor);
  rightLeg.position.y = -1.75 * v;
  rightLegPivot.add(rightLeg);
  const rightShoe = box(legWidth, 0.8, 1.4, def.shoeColor);
  rightShoe.position.set(0, -3.75 * v, 0.1 * v);
  rightLegPivot.add(rightShoe);
  rightLegPivot.position.set(0.6 * v, 4.0 * v, 0);
  bodyGroup.add(rightLegPivot);

  // --- Name tag ---
  const nameTag = createNameTag(def.name);
  nameTag.position.y = (4.0 + 3.2 + 3.6 + 1.6) * v;
  nameTag.name = 'nameTag';
  root.add(nameTag);

  return root;
}

// --- Animal builders ---

type AnimalType = 'pig' | 'cow' | 'bunny' | 'lamb' | 'red_panda' | 'parrot';

function buildPigMesh(): THREE.Group {
  const root = new THREE.Group();
  const v = 0.2; // voxel size

  function box(w: number, h: number, d: number, color: number): THREE.Mesh {
    const geometry = new THREE.BoxGeometry(w * v, h * v, d * v);
    const material = new THREE.MeshLambertMaterial({ color });
    return new THREE.Mesh(geometry, material);
  }

  const bodyGroup = new THREE.Group();
  bodyGroup.name = 'bodyGroup';
  root.add(bodyGroup);

  // Pink pig color
  const pigPink = 0xf5a0a0;
  const pigDarkPink = 0xd88888;
  const pigSnout = 0xe89090;

  // Body
  const body = box(5, 4, 7, pigPink);
  body.position.y = 2.5 * v;
  bodyGroup.add(body);

  // Head
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  const head = box(4, 4, 4, pigPink);
  headGroup.add(head);

  // Snout
  const snout = box(2, 1.5, 1, pigSnout);
  snout.position.set(0, -0.5 * v, 2.3 * v);
  headGroup.add(snout);

  // Nostrils
  const nostrilGeo = new THREE.BoxGeometry(0.3 * v, 0.3 * v, 0.15 * v);
  const nostrilMat = new THREE.MeshLambertMaterial({ color: pigDarkPink });
  const leftNostril = new THREE.Mesh(nostrilGeo, nostrilMat);
  leftNostril.position.set(-0.4 * v, -0.5 * v, 2.85 * v);
  headGroup.add(leftNostril);
  const rightNostril = new THREE.Mesh(nostrilGeo, nostrilMat);
  rightNostril.position.set(0.4 * v, -0.5 * v, 2.85 * v);
  headGroup.add(rightNostril);

  // Eyes
  const eyeGeo = new THREE.BoxGeometry(0.6 * v, 0.6 * v, 0.15 * v);
  const eyeMat = new THREE.MeshLambertMaterial({ color: 0x222222 });
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-1.0 * v, 0.5 * v, 2.0 * v);
  headGroup.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(1.0 * v, 0.5 * v, 2.0 * v);
  headGroup.add(rightEye);

  // Ears
  const earGeo = new THREE.BoxGeometry(1.5 * v, 1 * v, 0.4 * v);
  const earMat = new THREE.MeshLambertMaterial({ color: pigDarkPink });
  const leftEar = new THREE.Mesh(earGeo, earMat);
  leftEar.position.set(-1.5 * v, 2 * v, 0);
  leftEar.rotation.z = 0.3;
  headGroup.add(leftEar);
  const rightEar = new THREE.Mesh(earGeo, earMat);
  rightEar.position.set(1.5 * v, 2 * v, 0);
  rightEar.rotation.z = -0.3;
  headGroup.add(rightEar);

  headGroup.position.set(0, 2.5 * v, 4 * v);
  bodyGroup.add(headGroup);

  // Legs
  const legColor = pigDarkPink;
  const frontLeftLeg = new THREE.Group();
  frontLeftLeg.name = 'frontLeftLeg';
  const fl = box(1.2, 2, 1.2, legColor);
  fl.position.y = -1 * v;
  frontLeftLeg.add(fl);
  frontLeftLeg.position.set(-1.5 * v, 1.5 * v, 2.5 * v);
  bodyGroup.add(frontLeftLeg);

  const frontRightLeg = new THREE.Group();
  frontRightLeg.name = 'frontRightLeg';
  const fr = box(1.2, 2, 1.2, legColor);
  fr.position.y = -1 * v;
  frontRightLeg.add(fr);
  frontRightLeg.position.set(1.5 * v, 1.5 * v, 2.5 * v);
  bodyGroup.add(frontRightLeg);

  const backLeftLeg = new THREE.Group();
  backLeftLeg.name = 'backLeftLeg';
  const bl = box(1.2, 2, 1.2, legColor);
  bl.position.y = -1 * v;
  backLeftLeg.add(bl);
  backLeftLeg.position.set(-1.5 * v, 1.5 * v, -2.5 * v);
  bodyGroup.add(backLeftLeg);

  const backRightLeg = new THREE.Group();
  backRightLeg.name = 'backRightLeg';
  const br = box(1.2, 2, 1.2, legColor);
  br.position.y = -1 * v;
  backRightLeg.add(br);
  backRightLeg.position.set(1.5 * v, 1.5 * v, -2.5 * v);
  bodyGroup.add(backRightLeg);

  // Curly tail
  const tail = box(0.5, 0.5, 1, pigDarkPink);
  tail.position.set(0, 2.5 * v, -4 * v);
  tail.rotation.x = 0.5;
  bodyGroup.add(tail);

  return root;
}

function buildCowMesh(): THREE.Group {
  const root = new THREE.Group();
  const v = 0.22;

  function box(w: number, h: number, d: number, color: number): THREE.Mesh {
    const geometry = new THREE.BoxGeometry(w * v, h * v, d * v);
    const material = new THREE.MeshLambertMaterial({ color });
    return new THREE.Mesh(geometry, material);
  }

  const bodyGroup = new THREE.Group();
  bodyGroup.name = 'bodyGroup';
  root.add(bodyGroup);

  const cowWhite = 0xf5f5f0;
  const cowBrown = 0x4a3728;
  const cowPink = 0xeab8b8;

  // Body with spots
  const body = box(5, 5, 8, cowWhite);
  body.position.y = 3 * v;
  bodyGroup.add(body);

  // Brown spots
  const spot1 = box(2, 2, 0.1, cowBrown);
  spot1.position.set(-1.5 * v, 4 * v, 4.05 * v);
  bodyGroup.add(spot1);
  const spot2 = box(1.5, 1.5, 0.1, cowBrown);
  spot2.position.set(1 * v, 2 * v, 4.05 * v);
  bodyGroup.add(spot2);
  const spot3 = box(2, 2.5, 0.1, cowBrown);
  spot3.position.set(0, 3.5 * v, -4.05 * v);
  bodyGroup.add(spot3);

  // Head
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  const head = box(4, 4, 5, cowWhite);
  headGroup.add(head);

  // Brown face patch
  const facePatch = box(3, 2, 0.1, cowBrown);
  facePatch.position.set(0, 0.5 * v, 2.55 * v);
  headGroup.add(facePatch);

  // Snout/nose
  const snout = box(2.5, 1.5, 1, cowPink);
  snout.position.set(0, -1 * v, 2.5 * v);
  headGroup.add(snout);

  // Nostrils
  const nostrilMat = new THREE.MeshLambertMaterial({ color: 0x333333 });
  const nostrilGeo = new THREE.BoxGeometry(0.4 * v, 0.4 * v, 0.15 * v);
  const leftNostril = new THREE.Mesh(nostrilGeo, nostrilMat);
  leftNostril.position.set(-0.5 * v, -1 * v, 3.05 * v);
  headGroup.add(leftNostril);
  const rightNostril = new THREE.Mesh(nostrilGeo, nostrilMat);
  rightNostril.position.set(0.5 * v, -1 * v, 3.05 * v);
  headGroup.add(rightNostril);

  // Eyes
  const eyeMat = new THREE.MeshLambertMaterial({ color: 0x222222 });
  const eyeGeo = new THREE.BoxGeometry(0.6 * v, 0.6 * v, 0.15 * v);
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-1.2 * v, 1 * v, 2.5 * v);
  headGroup.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(1.2 * v, 1 * v, 2.5 * v);
  headGroup.add(rightEye);

  // Horns
  const hornMat = new THREE.MeshLambertMaterial({ color: 0xddc89a });
  const hornGeo = new THREE.BoxGeometry(0.5 * v, 1.5 * v, 0.5 * v);
  const leftHorn = new THREE.Mesh(hornGeo, hornMat);
  leftHorn.position.set(-1.5 * v, 2.5 * v, 0);
  leftHorn.rotation.z = 0.3;
  headGroup.add(leftHorn);
  const rightHorn = new THREE.Mesh(hornGeo, hornMat);
  rightHorn.position.set(1.5 * v, 2.5 * v, 0);
  rightHorn.rotation.z = -0.3;
  headGroup.add(rightHorn);

  // Ears
  const earMat = new THREE.MeshLambertMaterial({ color: cowWhite });
  const earGeo = new THREE.BoxGeometry(1 * v, 0.8 * v, 0.4 * v);
  const leftEar = new THREE.Mesh(earGeo, earMat);
  leftEar.position.set(-2.2 * v, 1.2 * v, 0);
  headGroup.add(leftEar);
  const rightEar = new THREE.Mesh(earGeo, earMat);
  rightEar.position.set(2.2 * v, 1.2 * v, 0);
  headGroup.add(rightEar);

  headGroup.position.set(0, 4 * v, 5.5 * v);
  bodyGroup.add(headGroup);

  // Legs
  const legColor = cowWhite;
  const frontLeftLeg = new THREE.Group();
  frontLeftLeg.name = 'frontLeftLeg';
  const fl = box(1.2, 3, 1.2, legColor);
  fl.position.y = -1.5 * v;
  frontLeftLeg.add(fl);
  frontLeftLeg.position.set(-1.5 * v, 2 * v, 3 * v);
  bodyGroup.add(frontLeftLeg);

  const frontRightLeg = new THREE.Group();
  frontRightLeg.name = 'frontRightLeg';
  const fr = box(1.2, 3, 1.2, legColor);
  fr.position.y = -1.5 * v;
  frontRightLeg.add(fr);
  frontRightLeg.position.set(1.5 * v, 2 * v, 3 * v);
  bodyGroup.add(frontRightLeg);

  const backLeftLeg = new THREE.Group();
  backLeftLeg.name = 'backLeftLeg';
  const bl = box(1.2, 3, 1.2, legColor);
  bl.position.y = -1.5 * v;
  backLeftLeg.add(bl);
  backLeftLeg.position.set(-1.5 * v, 2 * v, -3 * v);
  bodyGroup.add(backLeftLeg);

  const backRightLeg = new THREE.Group();
  backRightLeg.name = 'backRightLeg';
  const br = box(1.2, 3, 1.2, legColor);
  br.position.y = -1.5 * v;
  backRightLeg.add(br);
  backRightLeg.position.set(1.5 * v, 2 * v, -3 * v);
  bodyGroup.add(backRightLeg);

  // Tail
  const tail = box(0.4, 2, 0.4, cowBrown);
  tail.position.set(0, 2 * v, -4.5 * v);
  tail.rotation.x = 0.3;
  bodyGroup.add(tail);

  // Udder
  const udder = box(1.5, 1, 1.5, cowPink);
  udder.position.set(0, 0.5 * v, -1 * v);
  bodyGroup.add(udder);

  return root;
}

function buildBunnyMesh(): THREE.Group {
  const root = new THREE.Group();
  const v = 0.15;

  function box(w: number, h: number, d: number, color: number): THREE.Mesh {
    const geometry = new THREE.BoxGeometry(w * v, h * v, d * v);
    const material = new THREE.MeshLambertMaterial({ color });
    return new THREE.Mesh(geometry, material);
  }

  const bodyGroup = new THREE.Group();
  bodyGroup.name = 'bodyGroup';
  root.add(bodyGroup);

  const bunnyWhite = 0xf8f8f8;
  const bunnyPink = 0xffcccc;

  // Body (round)
  const body = box(3, 3, 4, bunnyWhite);
  body.position.y = 2 * v;
  bodyGroup.add(body);

  // Head
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  const head = box(3, 3, 3, bunnyWhite);
  headGroup.add(head);

  // Ears (long!)
  const leftEar = box(0.8, 4, 0.4, bunnyWhite);
  leftEar.position.set(-0.7 * v, 3 * v, 0);
  headGroup.add(leftEar);
  const leftEarInner = box(0.4, 3.5, 0.2, bunnyPink);
  leftEarInner.position.set(-0.7 * v, 3 * v, 0.15 * v);
  headGroup.add(leftEarInner);

  const rightEar = box(0.8, 4, 0.4, bunnyWhite);
  rightEar.position.set(0.7 * v, 3 * v, 0);
  headGroup.add(rightEar);
  const rightEarInner = box(0.4, 3.5, 0.2, bunnyPink);
  rightEarInner.position.set(0.7 * v, 3 * v, 0.15 * v);
  headGroup.add(rightEarInner);

  // Eyes
  const eyeMat = new THREE.MeshLambertMaterial({ color: 0xff4444 }); // red eyes
  const eyeGeo = new THREE.BoxGeometry(0.5 * v, 0.5 * v, 0.15 * v);
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.8 * v, 0.3 * v, 1.55 * v);
  headGroup.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.8 * v, 0.3 * v, 1.55 * v);
  headGroup.add(rightEye);

  // Nose
  const nose = box(0.5, 0.4, 0.3, bunnyPink);
  nose.position.set(0, -0.3 * v, 1.5 * v);
  headGroup.add(nose);

  headGroup.position.set(0, 2.5 * v, 2.5 * v);
  bodyGroup.add(headGroup);

  // Legs (small)
  const frontLeftLeg = new THREE.Group();
  frontLeftLeg.name = 'frontLeftLeg';
  const fl = box(0.8, 1.2, 0.8, bunnyWhite);
  fl.position.y = -0.6 * v;
  frontLeftLeg.add(fl);
  frontLeftLeg.position.set(-1 * v, 1 * v, 1.5 * v);
  bodyGroup.add(frontLeftLeg);

  const frontRightLeg = new THREE.Group();
  frontRightLeg.name = 'frontRightLeg';
  const fr = box(0.8, 1.2, 0.8, bunnyWhite);
  fr.position.y = -0.6 * v;
  frontRightLeg.add(fr);
  frontRightLeg.position.set(1 * v, 1 * v, 1.5 * v);
  bodyGroup.add(frontRightLeg);

  // Back legs (bigger for hopping)
  const backLeftLeg = new THREE.Group();
  backLeftLeg.name = 'backLeftLeg';
  const bl = box(1.2, 1.5, 2, bunnyWhite);
  bl.position.y = -0.75 * v;
  backLeftLeg.add(bl);
  backLeftLeg.position.set(-1 * v, 1.2 * v, -1.5 * v);
  bodyGroup.add(backLeftLeg);

  const backRightLeg = new THREE.Group();
  backRightLeg.name = 'backRightLeg';
  const br = box(1.2, 1.5, 2, bunnyWhite);
  br.position.y = -0.75 * v;
  backRightLeg.add(br);
  backRightLeg.position.set(1 * v, 1.2 * v, -1.5 * v);
  bodyGroup.add(backRightLeg);

  // Fluffy tail
  const tail = box(1.2, 1.2, 1.2, bunnyWhite);
  tail.position.set(0, 2 * v, -2.5 * v);
  bodyGroup.add(tail);

  return root;
}

function buildLambMesh(): THREE.Group {
  const root = new THREE.Group();
  const v = 0.18;

  function box(w: number, h: number, d: number, color: number): THREE.Mesh {
    const geometry = new THREE.BoxGeometry(w * v, h * v, d * v);
    const material = new THREE.MeshLambertMaterial({ color });
    return new THREE.Mesh(geometry, material);
  }

  const bodyGroup = new THREE.Group();
  bodyGroup.name = 'bodyGroup';
  root.add(bodyGroup);

  const woolWhite = 0xfafafa;
  const skinPink = 0xf0c0c0;
  const skinGray = 0x666666;

  // Fluffy wool body
  const body = box(4.5, 4, 6, woolWhite);
  body.position.y = 2.5 * v;
  bodyGroup.add(body);

  // Extra fluff
  const fluff1 = box(5, 2, 5, woolWhite);
  fluff1.position.set(0, 4 * v, 0);
  bodyGroup.add(fluff1);

  // Head
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  const head = box(3, 3, 3, skinPink);
  headGroup.add(head);

  // Wool on head
  const headWool = box(3.5, 2, 3, woolWhite);
  headWool.position.y = 1.2 * v;
  headGroup.add(headWool);

  // Ears
  const earMat = new THREE.MeshLambertMaterial({ color: skinPink });
  const earGeo = new THREE.BoxGeometry(1.5 * v, 0.6 * v, 0.4 * v);
  const leftEar = new THREE.Mesh(earGeo, earMat);
  leftEar.position.set(-1.8 * v, 0.5 * v, 0);
  leftEar.rotation.z = -0.5;
  headGroup.add(leftEar);
  const rightEar = new THREE.Mesh(earGeo, earMat);
  rightEar.position.set(1.8 * v, 0.5 * v, 0);
  rightEar.rotation.z = 0.5;
  headGroup.add(rightEar);

  // Eyes
  const eyeMat = new THREE.MeshLambertMaterial({ color: 0x222222 });
  const eyeGeo = new THREE.BoxGeometry(0.5 * v, 0.5 * v, 0.15 * v);
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.8 * v, 0.3 * v, 1.55 * v);
  headGroup.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.8 * v, 0.3 * v, 1.55 * v);
  headGroup.add(rightEye);

  // Nose
  const nose = box(0.8, 0.5, 0.3, skinGray);
  nose.position.set(0, -0.5 * v, 1.55 * v);
  headGroup.add(nose);

  headGroup.position.set(0, 3 * v, 4 * v);
  bodyGroup.add(headGroup);

  // Legs (skinny)
  const legColor = skinGray;
  const frontLeftLeg = new THREE.Group();
  frontLeftLeg.name = 'frontLeftLeg';
  const fl = box(0.8, 2.5, 0.8, legColor);
  fl.position.y = -1.25 * v;
  frontLeftLeg.add(fl);
  frontLeftLeg.position.set(-1.3 * v, 1.5 * v, 2 * v);
  bodyGroup.add(frontLeftLeg);

  const frontRightLeg = new THREE.Group();
  frontRightLeg.name = 'frontRightLeg';
  const fr = box(0.8, 2.5, 0.8, legColor);
  fr.position.y = -1.25 * v;
  frontRightLeg.add(fr);
  frontRightLeg.position.set(1.3 * v, 1.5 * v, 2 * v);
  bodyGroup.add(frontRightLeg);

  const backLeftLeg = new THREE.Group();
  backLeftLeg.name = 'backLeftLeg';
  const bl = box(0.8, 2.5, 0.8, legColor);
  bl.position.y = -1.25 * v;
  backLeftLeg.add(bl);
  backLeftLeg.position.set(-1.3 * v, 1.5 * v, -2 * v);
  bodyGroup.add(backLeftLeg);

  const backRightLeg = new THREE.Group();
  backRightLeg.name = 'backRightLeg';
  const br = box(0.8, 2.5, 0.8, legColor);
  br.position.y = -1.25 * v;
  backRightLeg.add(br);
  backRightLeg.position.set(1.3 * v, 1.5 * v, -2 * v);
  bodyGroup.add(backRightLeg);

  // Small wool tail
  const tail = box(1.5, 1.5, 1, woolWhite);
  tail.position.set(0, 2.5 * v, -3.5 * v);
  bodyGroup.add(tail);

  return root;
}

function buildRedPandaMesh(): THREE.Group {
  const root = new THREE.Group();
  const v = 0.16;

  function box(w: number, h: number, d: number, color: number): THREE.Mesh {
    const geometry = new THREE.BoxGeometry(w * v, h * v, d * v);
    const material = new THREE.MeshLambertMaterial({ color });
    return new THREE.Mesh(geometry, material);
  }

  const bodyGroup = new THREE.Group();
  bodyGroup.name = 'bodyGroup';
  root.add(bodyGroup);

  const redOrange = 0xcc4422;
  const darkBrown = 0x3d2817;
  const white = 0xffffff;
  const black = 0x111111;

  // Body
  const body = box(3.5, 3, 6, redOrange);
  body.position.y = 2 * v;
  bodyGroup.add(body);

  // Belly (darker)
  const belly = box(3, 2.5, 5, darkBrown);
  belly.position.set(0, 1.5 * v, 0);
  bodyGroup.add(belly);

  // Head
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  const head = box(3.5, 3, 3, redOrange);
  headGroup.add(head);

  // White face markings
  const faceMark1 = box(1, 1.5, 0.1, white);
  faceMark1.position.set(-0.8 * v, 0, 1.55 * v);
  headGroup.add(faceMark1);
  const faceMark2 = box(1, 1.5, 0.1, white);
  faceMark2.position.set(0.8 * v, 0, 1.55 * v);
  headGroup.add(faceMark2);

  // Ears
  const leftEar = box(1, 1.5, 0.5, redOrange);
  leftEar.position.set(-1.3 * v, 2 * v, 0);
  headGroup.add(leftEar);
  const leftEarInner = box(0.6, 1, 0.3, white);
  leftEarInner.position.set(-1.3 * v, 2 * v, 0.15 * v);
  headGroup.add(leftEarInner);

  const rightEar = box(1, 1.5, 0.5, redOrange);
  rightEar.position.set(1.3 * v, 2 * v, 0);
  headGroup.add(rightEar);
  const rightEarInner = box(0.6, 1, 0.3, white);
  rightEarInner.position.set(1.3 * v, 2 * v, 0.15 * v);
  headGroup.add(rightEarInner);

  // Eyes
  const eyeMat = new THREE.MeshLambertMaterial({ color: black });
  const eyeGeo = new THREE.BoxGeometry(0.6 * v, 0.6 * v, 0.15 * v);
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.8 * v, 0.3 * v, 1.55 * v);
  headGroup.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.8 * v, 0.3 * v, 1.55 * v);
  headGroup.add(rightEye);

  // Black nose
  const nose = box(0.6, 0.4, 0.3, black);
  nose.position.set(0, -0.5 * v, 1.55 * v);
  headGroup.add(nose);

  headGroup.position.set(0, 2.5 * v, 4 * v);
  bodyGroup.add(headGroup);

  // Legs (dark)
  const legColor = darkBrown;
  const frontLeftLeg = new THREE.Group();
  frontLeftLeg.name = 'frontLeftLeg';
  const fl = box(1, 2, 1, legColor);
  fl.position.y = -1 * v;
  frontLeftLeg.add(fl);
  frontLeftLeg.position.set(-1.2 * v, 1.2 * v, 2 * v);
  bodyGroup.add(frontLeftLeg);

  const frontRightLeg = new THREE.Group();
  frontRightLeg.name = 'frontRightLeg';
  const fr = box(1, 2, 1, legColor);
  fr.position.y = -1 * v;
  frontRightLeg.add(fr);
  frontRightLeg.position.set(1.2 * v, 1.2 * v, 2 * v);
  bodyGroup.add(frontRightLeg);

  const backLeftLeg = new THREE.Group();
  backLeftLeg.name = 'backLeftLeg';
  const bl = box(1, 2, 1, legColor);
  bl.position.y = -1 * v;
  backLeftLeg.add(bl);
  backLeftLeg.position.set(-1.2 * v, 1.2 * v, -2 * v);
  bodyGroup.add(backLeftLeg);

  const backRightLeg = new THREE.Group();
  backRightLeg.name = 'backRightLeg';
  const br = box(1, 2, 1, legColor);
  br.position.y = -1 * v;
  backRightLeg.add(br);
  backRightLeg.position.set(1.2 * v, 1.2 * v, -2 * v);
  bodyGroup.add(backRightLeg);

  // Big fluffy striped tail!
  const tailGroup = new THREE.Group();
  tailGroup.name = 'tail';
  for (let i = 0; i < 6; i++) {
    const segment = box(1.8, 1.8, 1.2, i % 2 === 0 ? redOrange : darkBrown);
    segment.position.z = -i * 1.1 * v;
    tailGroup.add(segment);
  }
  tailGroup.position.set(0, 2.5 * v, -4 * v);
  tailGroup.rotation.x = 0.3;
  bodyGroup.add(tailGroup);

  return root;
}

function buildParrotMesh(): THREE.Group {
  const root = new THREE.Group();
  const v = 0.12;

  function box(w: number, h: number, d: number, color: number): THREE.Mesh {
    const geometry = new THREE.BoxGeometry(w * v, h * v, d * v);
    const material = new THREE.MeshLambertMaterial({ color });
    return new THREE.Mesh(geometry, material);
  }

  const bodyGroup = new THREE.Group();
  bodyGroup.name = 'bodyGroup';
  root.add(bodyGroup);

  const red = 0xee3333;
  const blue = 0x3366ee;
  const yellow = 0xffdd00;
  const green = 0x33aa33;
  const white = 0xffffff;
  const black = 0x111111;
  const orange = 0xff8800;

  // Body
  const body = box(2.5, 3, 3, red);
  body.position.y = 2 * v;
  bodyGroup.add(body);

  // Blue wing markings
  const leftWing = box(0.3, 2.5, 2.5, blue);
  leftWing.position.set(-1.4 * v, 2 * v, 0);
  bodyGroup.add(leftWing);
  const rightWing = box(0.3, 2.5, 2.5, blue);
  rightWing.position.set(1.4 * v, 2 * v, 0);
  bodyGroup.add(rightWing);

  // Yellow on wings
  const leftWingYellow = box(0.3, 1, 2, yellow);
  leftWingYellow.position.set(-1.5 * v, 1 * v, -0.3 * v);
  bodyGroup.add(leftWingYellow);
  const rightWingYellow = box(0.3, 1, 2, yellow);
  rightWingYellow.position.set(1.5 * v, 1 * v, -0.3 * v);
  bodyGroup.add(rightWingYellow);

  // Head
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  const head = box(2.5, 2.5, 2.5, red);
  headGroup.add(head);

  // White face
  const face = box(2, 1.5, 0.2, white);
  face.position.set(0, 0, 1.3 * v);
  headGroup.add(face);

  // Beak (curved)
  const beak = box(1, 1.2, 1.5, black);
  beak.position.set(0, -0.5 * v, 1.5 * v);
  beak.rotation.x = 0.3;
  headGroup.add(beak);

  // Eyes
  const eyeMat = new THREE.MeshLambertMaterial({ color: black });
  const eyeGeo = new THREE.BoxGeometry(0.4 * v, 0.4 * v, 0.15 * v);
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.6 * v, 0.3 * v, 1.3 * v);
  headGroup.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.6 * v, 0.3 * v, 1.3 * v);
  headGroup.add(rightEye);

  headGroup.position.set(0, 4 * v, 1 * v);
  bodyGroup.add(headGroup);

  // Tail feathers (long, colorful)
  const tailGroup = new THREE.Group();
  const tail1 = box(0.5, 0.5, 5, red);
  tail1.position.set(-0.4 * v, 0, 0);
  tailGroup.add(tail1);
  const tail2 = box(0.5, 0.5, 5.5, blue);
  tail2.position.set(0, 0, 0);
  tailGroup.add(tail2);
  const tail3 = box(0.5, 0.5, 5, green);
  tail3.position.set(0.4 * v, 0, 0);
  tailGroup.add(tail3);
  tailGroup.position.set(0, 1 * v, -4 * v);
  tailGroup.rotation.x = -0.3;
  bodyGroup.add(tailGroup);

  // Feet
  const footMat = new THREE.MeshLambertMaterial({ color: orange });
  const footGeo = new THREE.BoxGeometry(0.8 * v, 1.5 * v, 0.8 * v);
  const leftFoot = new THREE.Mesh(footGeo, footMat);
  leftFoot.position.set(-0.6 * v, 0, 0.5 * v);
  bodyGroup.add(leftFoot);
  const rightFoot = new THREE.Mesh(footGeo, footMat);
  rightFoot.position.set(0.6 * v, 0, 0.5 * v);
  bodyGroup.add(rightFoot);

  return root;
}

function buildAnimalMesh(type: AnimalType): THREE.Group {
  switch (type) {
    case 'pig': return buildPigMesh();
    case 'cow': return buildCowMesh();
    case 'bunny': return buildBunnyMesh();
    case 'lamb': return buildLambMesh();
    case 'red_panda': return buildRedPandaMesh();
    case 'parrot': return buildParrotMesh();
  }
}

// --- Helper to get entity type's animal type ---
const ENTITY_TYPE_TO_ANIMAL: Record<number, AnimalType> = {
  [NPCEntityType.Pig]: 'pig',
  [NPCEntityType.Cow]: 'cow',
  [NPCEntityType.Bunny]: 'bunny',
  [NPCEntityType.Lamb]: 'lamb',
  [NPCEntityType.RedPanda]: 'red_panda',
  [NPCEntityType.Parrot]: 'parrot',
};

function getPartByName(group: THREE.Group, name: string): THREE.Group | null {
  let result: THREE.Group | null = null;
  group.traverse((child) => {
    if (child.name === name && child instanceof THREE.Group) {
      result = child;
    }
  });
  return result;
}

// --- Client NPC state (server-synced) ---

interface ClientNPC {
  id: number;
  entityType: number;
  isCharacter: boolean;
  mesh: THREE.Group;
  // Current (rendered) position
  x: number;
  y: number;
  z: number;
  facing: number;
  // Target (from server)
  targetX: number;
  targetY: number;
  targetZ: number;
  targetFacing: number;
  state: number; // 0=idle, 1=walking
  // Animation
  walkTime: number;
  prevX: number;
  prevZ: number;
  // Cached part references (avoid traverse every frame)
  cachedBodyGroup: THREE.Group | null;
  cachedLeftArm: THREE.Group | null;
  cachedRightArm: THREE.Group | null;
  cachedLeftLeg: THREE.Group | null;
  cachedRightLeg: THREE.Group | null;
  cachedFrontLeftLeg: THREE.Group | null;
  cachedFrontRightLeg: THREE.Group | null;
  cachedBackLeftLeg: THREE.Group | null;
  cachedBackRightLeg: THREE.Group | null;
}

// --- NPCManager (client, server-synced) ---

export class NPCManager {
  scene: THREE.Scene;
  private entities = new Map<number, ClientNPC>();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  // Called when server sends NPCSpawn message
  handleSpawn(spawns: NPCSpawnData[]) {
    for (const spawn of spawns) {
      if (this.entities.has(spawn.id)) continue;

      let mesh: THREE.Group;
      let isCharacter = false;

      if (spawn.entityType <= 8) {
        // Character NPC
        const def = CHARACTERS[spawn.entityType];
        if (!def) continue;
        mesh = buildCharacterMesh(def);
        isCharacter = true;
      } else {
        // Animal
        const animalType = ENTITY_TYPE_TO_ANIMAL[spawn.entityType];
        if (!animalType) continue;
        mesh = buildAnimalMesh(animalType);
      }

      mesh.position.set(spawn.x, spawn.y, spawn.z);
      const bodyGroup = getPartByName(mesh, 'bodyGroup');
      if (bodyGroup) {
        bodyGroup.rotation.y = spawn.facing;
      }

      this.scene.add(mesh);

      // Cache part references once (avoids traverse every frame)
      const npc: ClientNPC = {
        id: spawn.id,
        entityType: spawn.entityType,
        isCharacter,
        mesh,
        x: spawn.x,
        y: spawn.y,
        z: spawn.z,
        facing: spawn.facing,
        targetX: spawn.x,
        targetY: spawn.y,
        targetZ: spawn.z,
        targetFacing: spawn.facing,
        state: 0,
        walkTime: Math.random() * 10,
        prevX: spawn.x,
        prevZ: spawn.z,
        cachedBodyGroup: bodyGroup,
        cachedLeftArm: null,
        cachedRightArm: null,
        cachedLeftLeg: null,
        cachedRightLeg: null,
        cachedFrontLeftLeg: null,
        cachedFrontRightLeg: null,
        cachedBackLeftLeg: null,
        cachedBackRightLeg: null,
      };

      if (isCharacter) {
        npc.cachedLeftArm = getPartByName(mesh, 'leftArm');
        npc.cachedRightArm = getPartByName(mesh, 'rightArm');
        npc.cachedLeftLeg = getPartByName(mesh, 'leftLeg');
        npc.cachedRightLeg = getPartByName(mesh, 'rightLeg');
      } else {
        npc.cachedFrontLeftLeg = getPartByName(mesh, 'frontLeftLeg');
        npc.cachedFrontRightLeg = getPartByName(mesh, 'frontRightLeg');
        npc.cachedBackLeftLeg = getPartByName(mesh, 'backLeftLeg');
        npc.cachedBackRightLeg = getPartByName(mesh, 'backRightLeg');
      }

      this.entities.set(spawn.id, npc);
    }
  }

  // Called when server sends NPCPositions message
  handlePositions(positions: NPCPosData[]) {
    for (const pos of positions) {
      const npc = this.entities.get(pos.id);
      if (!npc) continue;
      npc.targetX = pos.x;
      npc.targetY = pos.y;
      npc.targetZ = pos.z;
      npc.targetFacing = pos.facing;
      npc.state = pos.state;
    }
  }

  update(dt: number) {
    const lerpFactor = Math.min(1, dt * 8);

    for (const npc of this.entities.values()) {
      // Interpolate position
      npc.x += (npc.targetX - npc.x) * lerpFactor;
      npc.y += (npc.targetY - npc.y) * lerpFactor;
      npc.z += (npc.targetZ - npc.z) * lerpFactor;

      // Interpolate facing angle (handle wrapping)
      let facingDiff = npc.targetFacing - npc.facing;
      while (facingDiff > Math.PI) facingDiff -= Math.PI * 2;
      while (facingDiff < -Math.PI) facingDiff += Math.PI * 2;
      npc.facing += facingDiff * lerpFactor;

      // Detect movement for walk animation
      const dx = npc.x - npc.prevX;
      const dz = npc.z - npc.prevZ;
      const isMoving = npc.state === 1;
      npc.prevX = npc.x;
      npc.prevZ = npc.z;

      if (npc.isCharacter) {
        // Character animation (arms + legs) — use cached refs
        const leftArm = npc.cachedLeftArm;
        const rightArm = npc.cachedRightArm;
        const leftLeg = npc.cachedLeftLeg;
        const rightLeg = npc.cachedRightLeg;

        if (isMoving) {
          npc.walkTime += dt;
          const swingAngle = Math.sin(npc.walkTime * 5) * 0.6;
          if (leftArm) leftArm.rotation.x = swingAngle;
          if (rightArm) rightArm.rotation.x = -swingAngle;
          if (leftLeg) leftLeg.rotation.x = -swingAngle * 0.7;
          if (rightLeg) rightLeg.rotation.x = swingAngle * 0.7;
        } else {
          if (leftArm) leftArm.rotation.x *= 0.9;
          if (rightArm) rightArm.rotation.x *= 0.9;
          if (leftLeg) leftLeg.rotation.x *= 0.9;
          if (rightLeg) rightLeg.rotation.x *= 0.9;
        }
      } else {
        // Animal animation (4 legs) — use cached refs
        const frontLeftLeg = npc.cachedFrontLeftLeg;
        const frontRightLeg = npc.cachedFrontRightLeg;
        const backLeftLeg = npc.cachedBackLeftLeg;
        const backRightLeg = npc.cachedBackRightLeg;

        if (isMoving) {
          npc.walkTime += dt;
          const legSpeed = npc.entityType === NPCEntityType.Bunny ? 8 : 5;
          const swingAngle = Math.sin(npc.walkTime * legSpeed) * 0.5;
          if (frontLeftLeg) frontLeftLeg.rotation.x = swingAngle;
          if (frontRightLeg) frontRightLeg.rotation.x = -swingAngle;
          if (backLeftLeg) backLeftLeg.rotation.x = -swingAngle;
          if (backRightLeg) backRightLeg.rotation.x = swingAngle;
        } else {
          if (frontLeftLeg) frontLeftLeg.rotation.x *= 0.9;
          if (frontRightLeg) frontRightLeg.rotation.x *= 0.9;
          if (backLeftLeg) backLeftLeg.rotation.x *= 0.9;
          if (backRightLeg) backRightLeg.rotation.x *= 0.9;
        }
      }

      // Bob/breathing animation
      const bobAmount = isMoving ? 0.03 : 0.06;
      const bobSpeed = isMoving ? 5 : 2;
      const bob = Math.sin(npc.walkTime * bobSpeed) * bobAmount;

      npc.mesh.position.set(npc.x, npc.y + bob, npc.z);

      if (npc.cachedBodyGroup) {
        npc.cachedBodyGroup.rotation.y = npc.facing;
      }
    }
  }

  getNearbyNPCs(position: THREE.Vector3, radius: number): { name: string; x: number; z: number }[] {
    const r2 = radius * radius;
    const result: { name: string; x: number; z: number }[] = [];
    for (const npc of this.entities.values()) {
      if (!npc.isCharacter) continue;
      const dx = npc.x - position.x;
      const dz = npc.z - position.z;
      if (dx * dx + dz * dz < r2) {
        const def = CHARACTERS[npc.entityType];
        if (def) result.push({ name: def.name, x: npc.x, z: npc.z });
      }
    }
    return result;
  }

  getCharacterMeshes(): THREE.Group[] {
    const meshes: THREE.Group[] = [];
    for (const npc of this.entities.values()) {
      if (npc.isCharacter) meshes.push(npc.mesh);
    }
    return meshes;
  }

  dispose() {
    for (const npc of this.entities.values()) {
      this.scene.remove(npc.mesh);
      npc.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) child.material.dispose();
        }
        if (child instanceof THREE.Sprite) {
          if (child.material.map) child.material.map.dispose();
          child.material.dispose();
        }
      });
    }
    this.entities.clear();
  }
}
